import { forwardRef, useState } from 'react';
import { Input, type InputProps } from '@klaro7/ui';

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 2.036 11.68a1.012 1.012 0 0 0 0 .639C3.423 16.49 7.36 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639a10.5 10.5 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  );
}

/**
 * Occhietto mostra/nascondi password (riscontro PEC 2026-07, punto 11).
 * Bottone standalone per i campi password "raw" delle pagine auth (login,
 * reset, change password) che hanno il proprio styling: va dentro un
 * contenitore `relative`, accanto a un input con `pr-10`.
 */
export function PasswordToggleButton({
  visible,
  onToggle,
  className,
}: {
  visible: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={visible ? 'Nascondi password' : 'Mostra password'}
      aria-pressed={visible}
      className={
        className ??
        'absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 transition-colors hover:text-neutral-600 dark:text-white/40 dark:hover:text-white/70'
      }
    >
      {visible ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
    </button>
  );
}

export type PasswordInputProps = Omit<InputProps, 'type' | 'rightIcon'>;

/**
 * Input password con occhietto, per i form che usano l'Input condiviso di
 * @klaro7/ui (registrazione, login form, cambio password nei profili).
 * Sfrutta lo slot rightIcon già cliccabile dell'Input.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(props, ref) {
    const [visible, setVisible] = useState(false);
    return (
      <Input
        {...props}
        ref={ref}
        type={visible ? 'text' : 'password'}
        rightIcon={
          <PasswordToggleButton
            visible={visible}
            onToggle={() => setVisible((v) => !v)}
            className="pointer-events-auto flex items-center text-neutral-400 transition-colors hover:text-neutral-600 dark:text-white/40 dark:hover:text-white/70"
          />
        }
      />
    );
  }
);
